import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ErrorBoundary from './ErrorBoundary.jsx';
import FindProgress from './FindProgress';
import { createEntryTimer, resolveCode } from '../features/pallets/palletCodes';
import {
  beaconWhereText, loadOpoChecklist, loadPalletFind, loadPlanChecklist, methodWord, opoChecklistLights,
  palletFindLights, planChecklistLights, rowTodo,
} from '../features/pallets/palletChecklist';

/**
 * The pallet checklist (2026-10-26; jobq-api main.py "PALLET HANDOFFS"): one line per pallet on an
 * outsourcing manifest, confirmed by SCANNING its beacon / label, TYPING the beacon or pallet number,
 * or TICKING it (Mike: scanning is an option, never required). Find all lights every beacon on the
 * list and shows where each is on the shop map (FindProgress).
 *
 * Steps: to_vendor (the pallets leave: pull each beacon, dock it on the shipping shelf, check the 4x6
 * label) and from_vendor (they came back: put each beacon back on). The box takes a USB scanner's
 * typing (data-own-scans: the app-wide ScanWedge leaves it alone) and records whether the entry came
 * at scanner speed (scan) or by hand (typed).
 *
 * onSubmit(items) does the PIN + the POST and answers {ok, message}; items = [{pallet_id, method, code}]
 * for the lines confirmed here (a line already recorded earlier is shown, never sent again). Without
 * onSubmit the list is for finding only (no record button).
 */
const styles = {
  row: { borderBottom: '1px solid var(--bs-border-color, #dee2e6)', padding: '10px 0', display: 'flex', gap: 12 },
  check: { width: 22, height: 22, marginTop: 2, flexShrink: 0 },
  text: { flex: 1, minWidth: 0 },
  title: { fontWeight: 700, fontFamily: 'monospace', fontSize: '1.05rem' },
  sub: { fontSize: '0.85rem', color: 'var(--bs-secondary-color, #6c757d)' },
  todo: { fontSize: '0.9rem', marginTop: 2 },
  chip: { display: 'inline-block', borderRadius: 999, padding: '1px 8px', fontSize: '0.75rem', fontWeight: 700, marginLeft: 6 },
  entry: { fontFamily: 'monospace', fontSize: '1.1rem' },
};

const CHIP = {
  scan: { background: '#0d6efd', color: '#fff' },
  typed: { background: '#6f42c1', color: '#fff' },
  tick: { background: '#198754', color: '#fff' },
  done: { background: 'var(--bs-secondary-bg, #e9ecef)', color: 'var(--bs-body-color, #212529)' },
};

// a scanned / typed pallet that is on the order but not one this list is for, by step
const ELSEWHERE_TEXT = {
  from_vendor: 'still has pieces at the vendor.',
  ship: 'is on this order but is not one of the pallets being shipped now.',
  pickup: 'is on this order but is not one of the pallets being picked up now.',
};

export const STEP_TITLES = {
  to_vendor: 'Pallets leaving for the vendor',
  from_vendor: 'Put the beacons back on',
  ship: 'Load the truck',
  pickup: 'Customer pickup: load the pallets',
  find: 'Find the pallets',
};

export function PalletChecklistModal({
  opoId = null, planId = null, palletIds = null, onlyPalletIds = null, step, initial = null, title = null,
  recordLabel = 'Record', requireAll = false, onSubmit = null, autoFind = false, onClose,
}) {
  const [checklist, setChecklist] = useState(initial);
  const [loadError, setLoadError] = useState('');
  const [confirmed, setConfirmed] = useState({}); // pallet_id -> {method, code}
  const [entry, setEntry] = useState('');
  const [note, setNote] = useState(null); // {tone, text}
  const [busy, setBusy] = useState('');
  const timer = useRef(createEntryTimer());
  const inputRef = useRef(null);

  useEffect(() => {
    if (initial) return undefined;
    let stop = false;
    const load = palletIds ? loadPalletFind(palletIds)
      : planId ? loadPlanChecklist(planId, step) : loadOpoChecklist(opoId, step);
    load.then((r) => {
      if (stop) return;
      if (r.ok && r.body) setChecklist(r.body);
      else setLoadError(r.message || 'Could not load the pallets.');
    });
    return () => { stop = true; };
    // palletIds is a fresh array each render of the caller: key on its contents
  }, [opoId, planId, palletIds && palletIds.join(','), step, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    let all = (checklist && checklist.pallets) || [];
    if (Array.isArray(onlyPalletIds)) {
      const keep = new Set(onlyPalletIds.map(Number));
      all = all.filter((r) => keep.has(Number(r.pallet_id)));
    }
    return step === 'from_vendor' ? all.filter((r) => r.back || r.done) : all;
  }, [checklist, step, onlyPalletIds]);
  const waiting = useMemo(() => (step === 'from_vendor' && checklist
    ? (checklist.pallets || []).filter((r) => !r.back && !r.done) : []), [checklist, step]);
  // a beacon on the shipping shelf (its pallet went to a vendor) is never lit by a plan's Find all
  const planStep = step === 'find' || step === 'ship' || step === 'pickup';
  const withBeacon = rows.filter((r) => r.beacon_id && !r.done && !(planStep && r.beacon_away));
  const isDone = (r) => !!(r.done || confirmed[r.pallet_id]);
  const doneCount = rows.filter(isDone).length;
  const newItems = rows.filter((r) => !r.done && confirmed[r.pallet_id])
    .map((r) => ({ pallet_id: r.pallet_id, ...confirmed[r.pallet_id] }));
  // requireAll: every pallet done; then even with nothing new to record (all recorded earlier) Record goes on
  const canRecord = !!onSubmit && !busy && (requireAll
    ? rows.length > 0 && doneCount === rows.length
    : newItems.length > 0);

  function confirmRow(r, method, code) {
    setConfirmed((c) => ({ ...c, [r.pallet_id]: { method, code: code || null } }));
  }

  function onEntryKey(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const text = entry.trim();
      const scan = timer.current.wasScan();
      timer.current.reset();
      if (!text) return;
      // resolved against EVERY pallet on the paper (as the API does), then told when it isn't one asked about
      const all = (checklist && checklist.pallets) || [];
      const hit = resolveCode(text, all);
      const row = hit && rows.find((r) => Number(r.pallet_id) === hit.palletId);
      const elsewhere = hit && !row && all.find((r) => Number(r.pallet_id) === hit.palletId);
      if (elsewhere) {
        setNote({ tone: 'warning', text: ELSEWHERE_TEXT[step]
          ? `${elsewhere.short_id} ${ELSEWHERE_TEXT[step]}` : `${elsewhere.short_id} is not on this list.` });
      } else if (!row) {
        setNote({ tone: 'danger', text: `"${text}" is not a pallet or beacon on this list.` });
      } else if (isDone(row)) {
        setNote({ tone: 'secondary', text: `${row.short_id} is already checked.` });
      } else {
        confirmRow(row, scan ? 'scan' : 'typed', text);
        setNote({ tone: 'success', text: `${row.short_id} ✓ (${scan ? 'scanned' : 'typed'})` });
      }
      setEntry('');
      return;
    }
    timer.current.key(e);
  }

  function toggle(r) {
    if (r.done) return;
    setConfirmed((c) => {
      const next = { ...c };
      if (next[r.pallet_id]) delete next[r.pallet_id];
      else next[r.pallet_id] = { method: 'tick', code: null };
      return next;
    });
    // the scanner types into the box: keep it there after a tick, or the next scan is lost
    if (inputRef.current) inputRef.current.focus();
  }

  async function lights(action) {
    setBusy(action);
    setNote(null);
    const r = palletIds
      ? await palletFindLights(palletIds, action)
      : planId
        ? await planChecklistLights(planId, step, action, onlyPalletIds)
        : await opoChecklistLights(opoId, step, action);
    setBusy('');
    if (r.ok && r.body) {
      setChecklist(r.body);
      if (action === 'find' && !r.body.beacons_on) {
        setNote({ tone: 'warning', text: 'Pallet beacons are switched off: nothing was lit. Find the pallets by their labels.' });
      }
    } else {
      setNote({ tone: 'danger', text: r.message || 'Could not light the beacons.' });
    }
    if (inputRef.current) inputRef.current.focus();
  }

  const autoFound = useRef(false);
  useEffect(() => {
    if (!autoFind || autoFound.current || !checklist) return;
    autoFound.current = true;
    if (withBeacon.length) lights('find');
  }, [autoFind, checklist]); // eslint-disable-line react-hooks/exhaustive-deps

  async function record() {
    if (!canRecord) return;
    setBusy('record');
    setNote(null);
    let r;
    try {
      r = await onSubmit(newItems);
    } catch (err) {
      r = { ok: false, message: (err && err.message) || 'Recording failed.' };
    }
    setBusy('');
    if (r && r.ok) {
      onClose({ recorded: true, result: r });
    } else if (r && r.cancelled) {
      // the PIN prompt was cancelled: stay on the list
    } else {
      setNote({ tone: 'danger', text: (r && r.message) || 'Recording failed.' });
    }
  }

  const heading = title || STEP_TITLES[step] || 'Pallets';
  return (
    <Modal show onHide={() => onClose({ recorded: false })} size="lg" fullscreen="md-down" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {heading}
          {checklist && (
            <div style={styles.sub}>
              {checklist.opo_number || checklist.so_number}
              {checklist.vendor ? ` · ${checklist.vendor}` : ''}
              {!checklist.vendor && checklist.customer ? ` · ${checklist.customer}` : ''}
            </div>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadError && <div className="alert alert-danger py-2">{loadError}</div>}
        {!checklist && !loadError && <div className="text-muted">Loading the pallets…</div>}
        {checklist && (
          <>
            <div className="mb-2" style={{ fontSize: '0.95rem' }}>{checklist.instruction}</div>
            <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
              <Button variant="primary" size="sm" disabled={!!busy || !withBeacon.length} onClick={() => lights('find')}
                data-testid="pallet-checklist-find-all">
                {busy === 'find' ? 'Lighting…' : `Find all (${withBeacon.length} beacon${withBeacon.length === 1 ? '' : 's'})`}
              </Button>
              <Button variant="outline-secondary" size="sm" disabled={!!busy || !withBeacon.length} onClick={() => lights('off')}>
                Lights off
              </Button>
              {!checklist.recording && onSubmit && (
                <span className="text-danger small">Checklists aren't set up on this server yet.</span>
              )}
            </div>
            {onSubmit && (
            <input
              ref={inputRef}
              data-own-scans
              data-testid="pallet-checklist-entry"
              className="form-control mb-1"
              style={styles.entry}
              placeholder="Scan, or type a beacon (BCN-20) or pallet (PLT-42C7) number, then Enter"
              value={entry}
              autoFocus
              onChange={(e) => setEntry(e.target.value)}
              onKeyDown={onEntryKey}
            />
            )}
            <div className={`small mb-2 text-${note ? note.tone : 'muted'}`} role="status" style={{ minHeight: 20 }}>
              {note ? note.text : (onSubmit ? 'Or tick each pallet as it is done.' : '')}
            </div>
            {!rows.length && (
              <div className="text-muted">
                {step === 'from_vendor' ? 'No pallet on this paper has come back with a beacon to put back on.'
                  : planId ? 'No pallet on this order is at that step.' : 'This paper names no pallet.'}
              </div>
            )}
            {rows.map((r) => {
              const c = confirmed[r.pallet_id];
              const find = r.command_id && r.beacon_id ? { command_id: r.command_id, beacon_id: r.beacon_id } : null;
              return (
                <div key={r.pallet_id} style={styles.row} data-testid="pallet-checklist-row">
                  {onSubmit && (
                    <input type="checkbox" style={styles.check} checked={isDone(r)} disabled={!!r.done}
                      aria-label={`${r.short_id} done`} onChange={() => toggle(r)} />
                  )}
                  <div style={styles.text}>
                    <div>
                      <span style={styles.title}>{r.short_id}</span>
                      {r.beacon_id && <span style={{ ...styles.chip, background: '#212529', color: '#fff' }}>{r.beacon_id}</span>}
                      {c && <span style={{ ...styles.chip, ...CHIP[c.method] }}>{methodWord(c.method)}</span>}
                      {r.done && (
                        <span style={{ ...styles.chip, ...CHIP.done }}>
                          {methodWord(r.done.method)}{r.done.by ? ` by ${r.done.by}` : ''}
                        </span>
                      )}
                    </div>
                    <div style={styles.sub}>
                      {[r.so_number, r.customer, r.pieces != null ? `${r.pieces} pcs` : null, r.size]
                        .filter(Boolean).join(' · ')}
                      {beaconWhereText(r) ? ` · ${beaconWhereText(r)}` : ''}
                    </div>
                    {!r.done && <div style={styles.todo}>{rowTodo(r, step)}</div>}
                    {find && <FindProgress find={find} beaconId={r.beacon_id} className="mt-1" />}
                  </div>
                </div>
              );
            })}
            {waiting.length > 0 && (
              <div className="small text-muted mt-2">
                Still at the vendor: {waiting.map((r) => r.short_id).join(', ')}
              </div>
            )}
            {checklist.loose && checklist.loose.length > 0 && (
              <div className="small text-muted mt-2">
                On the paper but on no pallet: {checklist.loose.map((x) => `${x.part} x${x.qty}`).join(', ')}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <span className="me-auto small text-muted" data-testid="pallet-checklist-count">
          {onSubmit && rows.length ? `${doneCount} of ${rows.length} done` : ''}
          {onSubmit && requireAll && rows.length > 0 && doneCount < rows.length ? ' · every pallet must be checked' : ''}
        </span>
        <Button variant="outline-secondary" onClick={() => onClose({ recorded: false })}>
          {onSubmit ? 'Cancel' : 'Close'}
        </Button>
        {onSubmit && (
          <Button variant="success" disabled={!canRecord} onClick={record} data-testid="pallet-checklist-record">
            {busy === 'record' ? 'Recording…' : recordLabel}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

/** Open the checklist in its own React root (like palletBeaconsPrompt); resolves {recorded, result}. */
export default function palletChecklistPrompt(props) {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      root.unmount();
      container.remove();
      resolve(value);
    };
    root.render(
      <ErrorBoundary
        fallback={(error) => (
          <div className="alert alert-danger m-3" role="alert" style={{ position: 'fixed', zIndex: 2001, top: 0, left: 0, right: 0 }}>
            The pallet checklist hit an error: {(error && error.message) || String(error)}.
            <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={() => finish({ recorded: false })}>Close</button>
          </div>
        )}
      >
        <PalletChecklistModal {...props} onClose={finish} />
      </ErrorBoundary>
    );
  });
}
